// Finance Books — Masters (Customers, Vendors, Items, Chart of Accounts).
// Each entity is rendered with a generic List + Form pattern.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import fb from '../api';
import {
  PageHeader, Btn, Drawer, Input, Select, Textarea, Table, Pill,
  fmtMoney, fmtDate,
} from '../ui';

// ────────────────────────────────────────────────────────────────────────────
// CUSTOMERS
// ────────────────────────────────────────────────────────────────────────────
export function Customers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const reload = async () => setRows(await fb.get('/customers'));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.name) return toast.error('Name required');
      if (edit) await fb.put(`/customers/${edit.id}`, form);
      else await fb.post('/customers', form);
      toast.success('Saved');
      setOpen(false); setEdit(null); setForm({}); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const onEdit = (c) => { setEdit(c); setForm(c); setOpen(true); };
  const onDelete = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await fb.del(`/customers/${c.id}`); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="fb-customers">
      <PageHeader
        title="Customers"
        subtitle={`${rows.length} customers · live receivable tracking`}
        right={<Btn variant="accent" onClick={() => { setEdit(null); setForm({ credit_period_days: 30, currency: 'INR' }); setOpen(true); }} data-testid="fb-new-customer">+ New Customer</Btn>}
      />
      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="text-white/55 text-xs font-mono">{r.code}</span> },
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'gstin', label: 'GSTIN', render: (r) => r.gstin || <span className="text-white/30">—</span> },
          { key: 'state', label: 'State' },
          { key: 'outstanding', label: 'Outstanding', align: 'right', render: (r) => <span className="text-orange-300">{fmtMoney(r.outstanding)}</span> },
          { key: 'actions', label: '', render: (r) => (
              <div className="flex gap-2 justify-end">
                <button className="text-xs text-white/50 hover:text-white" onClick={() => onEdit(r)}>Edit</button>
                <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={() => onDelete(r)}>Delete</button>
              </div>) },
        ]}
        rows={rows}
        empty="No customers yet. Click New Customer to begin."
        testidPrefix="customer"
      />
      <Drawer open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.name}` : 'New Customer'}>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name" />
          <Input label="Contact Person" value={form.contact_person || ''} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          <Input label="Email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="GSTIN" value={form.gstin || ''} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
          <Input label="PAN" value={form.pan || ''} onChange={(e) => setForm({ ...form, pan: e.target.value })} />
          <Input label="State" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          <Input label="State Code (e.g. 27 for MH)" value={form.state_code || ''} onChange={(e) => setForm({ ...form, state_code: e.target.value })} />
          <Input label="Credit Period (days)" type="number" value={form.credit_period_days ?? 30} onChange={(e) => setForm({ ...form, credit_period_days: Number(e.target.value) })} />
          <Input label="Credit Limit" type="number" value={form.credit_limit ?? 0} onChange={(e) => setForm({ ...form, credit_limit: Number(e.target.value) })} />
          <Input label="Opening Balance" type="number" value={form.opening_balance ?? 0} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} />
          <Input label="Currency" value={form.currency || 'INR'} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <Textarea label="Billing Address" value={form.billing_address || ''} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
          <Textarea label="Shipping Address" value={form.shipping_address || ''} onChange={(e) => setForm({ ...form, shipping_address: e.target.value })} />
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="cust-save">Save Customer</Btn>
        </div>
      </Drawer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// VENDORS
// ────────────────────────────────────────────────────────────────────────────
export function Vendors() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const reload = async () => setRows(await fb.get('/vendors'));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.name) return toast.error('Name required');
      if (edit) await fb.put(`/vendors/${edit.id}`, form);
      else await fb.post('/vendors', form);
      toast.success('Saved');
      setOpen(false); setEdit(null); setForm({}); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const onEdit = (v) => { setEdit(v); setForm(v); setOpen(true); };
  const onDelete = async (v) => {
    if (!window.confirm(`Delete ${v.name}?`)) return;
    try { await fb.del(`/vendors/${v.id}`); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="fb-vendors">
      <PageHeader
        title="Vendors"
        subtitle={`${rows.length} vendors · live payable tracking`}
        right={<Btn variant="accent" onClick={() => { setEdit(null); setForm({ payment_terms_days: 30, currency: 'INR' }); setOpen(true); }} data-testid="fb-new-vendor">+ New Vendor</Btn>}
      />
      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="text-white/55 text-xs font-mono">{r.code}</span> },
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'gstin', label: 'GSTIN' },
          { key: 'state', label: 'State' },
          { key: 'payable', label: 'Payable', align: 'right', render: (r) => <span className="text-rose-300">{fmtMoney(r.payable)}</span> },
          { key: 'actions', label: '', render: (r) => (
              <div className="flex gap-2 justify-end">
                <button className="text-xs text-white/50 hover:text-white" onClick={() => onEdit(r)}>Edit</button>
                <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={() => onDelete(r)}>Delete</button>
              </div>) },
        ]}
        rows={rows}
        empty="No vendors yet."
        testidPrefix="vendor"
      />
      <Drawer open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.name}` : 'New Vendor'}>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vend-name" />
          <Input label="Contact" value={form.contact_person || ''} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
          <Input label="Email" type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Phone" value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <Input label="GSTIN" value={form.gstin || ''} onChange={(e) => setForm({ ...form, gstin: e.target.value })} />
          <Input label="PAN" value={form.pan || ''} onChange={(e) => setForm({ ...form, pan: e.target.value })} />
          <Input label="State" value={form.state || ''} onChange={(e) => setForm({ ...form, state: e.target.value })} />
          <Input label="State Code" value={form.state_code || ''} onChange={(e) => setForm({ ...form, state_code: e.target.value })} />
          <Input label="Payment Terms (days)" type="number" value={form.payment_terms_days ?? 30} onChange={(e) => setForm({ ...form, payment_terms_days: Number(e.target.value) })} />
          <Input label="Opening Balance" type="number" value={form.opening_balance ?? 0} onChange={(e) => setForm({ ...form, opening_balance: Number(e.target.value) })} />
          <Input label="Bank Account" value={form.bank_account || ''} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} />
          <Input label="IFSC" value={form.bank_ifsc || ''} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} />
          <Input label="TDS Section" value={form.tds_section || ''} onChange={(e) => setForm({ ...form, tds_section: e.target.value })} />
          <Input label="TDS Rate %" type="number" value={form.tds_rate ?? 0} onChange={(e) => setForm({ ...form, tds_rate: Number(e.target.value) })} />
        </div>
        <Textarea label="Address" value={form.address || ''} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="vend-save">Save Vendor</Btn>
        </div>
      </Drawer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ITEMS
// ────────────────────────────────────────────────────────────────────────────
export function Items() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const reload = async () => setRows(await fb.get('/items'));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.name) return toast.error('Name required');
      const payload = { ...form, gst_pct: Number(form.gst_pct ?? 18) };
      if (edit) await fb.put(`/items/${edit.id}`, payload);
      else await fb.post('/items', payload);
      toast.success('Saved');
      setOpen(false); setEdit(null); setForm({}); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const onDelete = async (it) => {
    if (!window.confirm(`Delete ${it.name}?`)) return;
    try { await fb.del(`/items/${it.id}`); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div data-testid="fb-items">
      <PageHeader
        title="Items"
        subtitle={`${rows.length} items · goods + services · weighted-average costing`}
        right={<Btn variant="accent" onClick={() => { setEdit(null); setForm({ type: 'goods', unit: 'Nos', gst_pct: 18, track_inventory: true }); setOpen(true); }} data-testid="fb-new-item">+ New Item</Btn>}
      />
      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="text-white/55 text-xs font-mono">{r.code}</span> },
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-white">{r.name}</span> },
          { key: 'type', label: 'Type', render: (r) => <Pill tone={r.type === 'goods' ? 'blue' : 'orange'}>{r.type}</Pill> },
          { key: 'hsn_sac', label: 'HSN/SAC' },
          { key: 'selling_price', label: 'Sell', align: 'right', render: (r) => fmtMoney(r.selling_price) },
          { key: 'purchase_price', label: 'Cost', align: 'right', render: (r) => fmtMoney(r.purchase_price) },
          { key: 'gst_pct', label: 'GST', align: 'right', render: (r) => `${r.gst_pct}%` },
          { key: 'stock_on_hand', label: 'Stock', align: 'right', render: (r) => r.track_inventory ? <span className={r.stock_on_hand <= (r.reorder_level || 0) ? 'text-orange-300' : 'text-emerald-300'}>{r.stock_on_hand}</span> : <span className="text-white/30">—</span> },
          { key: 'actions', label: '', render: (r) => (
              <div className="flex gap-2 justify-end">
                <button className="text-xs text-white/50 hover:text-white" onClick={() => { setEdit(r); setForm(r); setOpen(true); }}>Edit</button>
                <button className="text-xs text-rose-400/70 hover:text-rose-400" onClick={() => onDelete(r)}>Delete</button>
              </div>) },
        ]}
        rows={rows}
        empty="No items yet."
        testidPrefix="item"
      />
      <Drawer open={open} onClose={() => setOpen(false)} title={edit ? `Edit ${edit.name}` : 'New Item'} width="max-w-2xl">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="item-name" />
          <Input label="SKU" value={form.sku || ''} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          <Select label="Type *" value={form.type || 'goods'} onChange={(e) => setForm({ ...form, type: e.target.value, track_inventory: e.target.value === 'goods' })}>
            <option value="goods">Goods</option>
            <option value="service">Service</option>
          </Select>
          <Input label="Unit" value={form.unit || 'Nos'} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          <Input label="HSN/SAC" value={form.hsn_sac || ''} onChange={(e) => setForm({ ...form, hsn_sac: e.target.value })} />
          <Input label="GST %" type="number" value={form.gst_pct ?? 18} onChange={(e) => setForm({ ...form, gst_pct: Number(e.target.value) })} />
          <Input label="Selling Price" type="number" value={form.selling_price ?? 0} onChange={(e) => setForm({ ...form, selling_price: Number(e.target.value) })} />
          <Input label="Purchase Price" type="number" value={form.purchase_price ?? 0} onChange={(e) => setForm({ ...form, purchase_price: Number(e.target.value) })} />
          {form.type === 'goods' && <>
            <Input label="Opening Stock" type="number" value={form.opening_stock ?? 0} onChange={(e) => setForm({ ...form, opening_stock: Number(e.target.value) })} />
            <Input label="Reorder Level" type="number" value={form.reorder_level ?? 0} onChange={(e) => setForm({ ...form, reorder_level: Number(e.target.value) })} />
          </>}
        </div>
        <Textarea label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save} data-testid="item-save">Save Item</Btn>
        </div>
      </Drawer>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CHART OF ACCOUNTS
// ────────────────────────────────────────────────────────────────────────────
export function ChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: 'expense' });
  const reload = async () => setRows(await fb.get('/accounts'));
  useEffect(() => { reload(); }, []);

  const save = async () => {
    try {
      if (!form.code || !form.name) return toast.error('Code and name required');
      await fb.post('/accounts', form);
      toast.success('Account created');
      setOpen(false); setForm({ type: 'expense' }); reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div data-testid="fb-coa">
      <PageHeader
        title="Chart of Accounts"
        subtitle={`${rows.length} accounts · auto-seeded with standard Indian accounting structure`}
        right={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="fb-new-account">+ New Account</Btn>}
      />
      <Table
        columns={[
          { key: 'code', label: 'Code', render: (r) => <span className="font-mono text-xs text-white/60">{r.code}</span> },
          { key: 'name', label: 'Name', render: (r) => <span className="text-white">{r.name}{r.system && <span className="ml-2 text-[9px] tracking-[0.2em] uppercase text-white/30">system</span>}</span> },
          { key: 'type', label: 'Type', render: (r) => <Pill tone={({ asset: 'green', liability: 'orange', equity: 'blue', income: 'green', expense: 'red' })[r.type]}>{r.type}</Pill> },
          { key: 'subtype', label: 'Sub-type', render: (r) => <span className="text-white/55 text-xs">{r.subtype}</span> },
          { key: 'balance', label: 'Balance', align: 'right', render: (r) => <span className="tabular-nums">{fmtMoney(r.balance)}</span> },
        ]}
        rows={rows}
        testidPrefix="coa"
      />
      <Drawer open={open} onClose={() => setOpen(false)} title="New Account" width="max-w-lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Code *" value={form.code || ''} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Select label="Type *" value={form.type || 'expense'} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            <option value="asset">Asset</option>
            <option value="liability">Liability</option>
            <option value="equity">Equity</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
          </Select>
          <Input label="Name *" value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input label="Sub-type" value={form.subtype || ''} onChange={(e) => setForm({ ...form, subtype: e.target.value })} />
        </div>
        <Textarea label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={save}>Save Account</Btn>
        </div>
      </Drawer>
    </div>
  );
}
